import React, { useCallback, useState } from 'react';

import { IconButton } from '@mui/material';
import { Lock, LockOpen } from 'lucide-react';
import { AlineTooltip } from 'src/components/generic/AlineTooltip';
import { ConfirmationDialog } from 'src/components/generic/ConfirmationDialog';
import { useIsViewer } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { useCheckWorkflowAuthorization, useGetWorkflowSimple } from 'src/hooks';

import { getMakeInternalMessage, SHARE_WITH_EVERYONE_MESSAGE } from './workflow-file-lock';

const MAKE_INTERNAL_TOOLTIP =
  'This file is accessible to everyone. Click to make it internal only.';
const INTERNAL_TOOLTIP =
  'This file is internal. Only your org sees it in the workflow. Click to make it accessible to everyone.';

interface WorkflowFileLockButtonProps {
  className: string;
  file: Pick<Apollo.WorkflowFileSimpleFragment, 'documentRole' | 'id' | 'isInternal'> & {
    document: Pick<Apollo.WorkflowFileSimpleFragment['document'], 'id'>;
  };
  workflowId: UUID;
}

// The change a confirmation is asking about, fixed when the dialog opens.
interface PendingChange {
  message: string;
  nextIsInternal: boolean;
}

/**
 * Locks a General document so only the owner's org sees it in the workflow, or shares it again.
 * Either way a confirmation comes first; when locking a file outsiders can already open, it names
 * them before their access is removed. Only editors in the owner's org get the button.
 */
export const WorkflowFileLockButton = React.memo(function WorkflowFileLockButton({
  className,
  file,
  workflowId,
}: WorkflowFileLockButtonProps): JSX.Element | null {
  const isViewer = useIsViewer();
  const { canEditWorkflow } = useCheckWorkflowAuthorization(workflowId);
  const { workflow } = useGetWorkflowSimple(workflowId);
  const [pendingChange, setPendingChange] = useState<PendingChange | null>(null);
  const [getExternalAccess, { loading: loadingAccess }] =
    Apollo.useGetWorkflowFileExternalAccessLazyQuery({ fetchPolicy: 'network-only' });
  const [setWorkflowFileInternal, { loading: saving }] =
    Apollo.useSetWorkflowFileInternalMutation();
  const { id: workflowFileId, isInternal } = file;
  const documentId = file.document.id;

  const handleClick = useCallback(async () => {
    if (isInternal) {
      setPendingChange({ message: SHARE_WITH_EVERYONE_MESSAGE, nextIsInternal: false });
      return;
    }

    const { data } = await getExternalAccess({ variables: { workflowFileId } });
    setPendingChange({
      message: getMakeInternalMessage(data?.workflowFileExternalAccess ?? []),
      nextIsInternal: true,
    });
  }, [getExternalAccess, isInternal, workflowFileId]);

  const handleConfirm = useCallback(async () => {
    if (!pendingChange) return;

    const { nextIsInternal } = pendingChange;
    setPendingChange(null);
    await setWorkflowFileInternal({
      optimisticResponse: {
        __typename: 'Mutation',
        setWorkflowFileInternal: {
          __typename: 'WorkflowFile',
          document: {
            __typename: 'Document',
            id: documentId,
            isInternalOnWorkflow: nextIsInternal,
          },
          id: workflowFileId,
          isInternal: nextIsInternal,
        },
      },
      variables: { input: { isInternal: nextIsInternal, workflowFileId } },
    });
  }, [documentId, pendingChange, setWorkflowFileInternal, workflowFileId]);

  const handleCloseConfirm = useCallback(() => setPendingChange(null), []);
  const handleClickButton = useCallback(() => void handleClick(), [handleClick]);

  const canChangeVisibility =
    canEditWorkflow &&
    !isViewer &&
    Boolean(workflow?.requesterInOwnerOrg) &&
    file.documentRole === Apollo.WorkflowFileDocumentRole.General;
  if (!canChangeVisibility) return null;

  const tooltip = isInternal ? INTERNAL_TOOLTIP : MAKE_INTERNAL_TOOLTIP;
  const isSharing = pendingChange?.nextIsInternal === false;

  return (
    <>
      <AlineTooltip arrow placement="top" title={tooltip}>
        <IconButton
          aria-label={tooltip}
          aria-pressed={isInternal}
          className={className}
          data-cy="workflow-file-lock-button"
          disabled={loadingAccess || saving}
          size="small"
          onClick={handleClickButton}
        >
          <LockIcon isInternal={isInternal} />
        </IconButton>
      </AlineTooltip>
      <ConfirmationDialog
        message={pendingChange?.message ?? ''}
        open={Boolean(pendingChange)}
        primaryText={isSharing ? 'Share' : 'Make Internal'}
        title={isSharing ? 'Share with Everyone?' : 'Make Internal?'}
        onClickPrimary={handleConfirm}
        onClose={handleCloseConfirm}
      />
    </>
  );
});

function LockIcon({ isInternal }: { isInternal: boolean }): JSX.Element {
  if (isInternal) return <Lock size={14} />;
  return <LockOpen size={14} />;
}
